/**
 * UserContextProvider Store - Zustand store with individual selectors
 * Following individual selectors pattern (no destructuring)
 */

import { create } from 'zustand';
import type { UserContextData } from '@/application/queries/userContext';

// ============================================================================
// Store State
// ============================================================================

interface UserContextState {
  // Data
  data: UserContextData | null;
  loading: boolean;
  error: Error | null;

  // Actions
  setData: (data: UserContextData) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
  _reset: () => void; // For testing
}

const initialState = {
  data: null,
  loading: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

const useUserContextStore = create<UserContextState>((set) => ({
  ...initialState,

  setData: (data) => set({ data }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  reset: () => set(initialState),
  _reset: () => set(initialState),
}));

// ============================================================================
// Individual Selector Hooks (per zustand-patterns.md)
// ============================================================================

// Empty array constant to prevent infinite re-renders
const EMPTY_ROLES: string[] = [];

// Data selectors
export const useUserContextData = () => useUserContextStore((state) => state.data);
export const useUserId = () => useUserContextStore((state) => state.data?.userId);
export const useUserName = () => useUserContextStore((state) => state.data?.userName);
export const useFirstName = () => useUserContextStore((state) => state.data?.firstName);
export const useLastName = () => useUserContextStore((state) => state.data?.lastName);
export const useRoles = () => useUserContextStore((state) => state.data?.roles ?? EMPTY_ROLES);
export const useIsReadOnly = () => useUserContextStore((state) => state.data?.isReadOnly ?? true);
export const useTenant = () => useUserContextStore((state) => state.data?.tenant);
export const useTenantNetworkId = () => useUserContextStore((state) => state.data?.tenant?.networkId);
export const useBilling = () => useUserContextStore((state) => state.data?.billing);
export const useNetworkOwnerType = () => useUserContextStore((state) => state.data?.networkOwnerType);
export const useCsrfToken = () => useUserContextStore((state) => state.data?.csrfToken);
export const useTimezoneOffset = () => useUserContextStore((state) => state.data?.timezoneOffset);

// State selectors
export const useUserContextLoading = () => useUserContextStore((state) => state.loading);
export const useUserContextError = () => useUserContextStore((state) => state.error);

// Action selectors
export const useSetUserContextData = () => useUserContextStore((state) => state.setData);
export const useSetUserContextLoading = () => useUserContextStore((state) => state.setLoading);
export const useSetUserContextError = () => useUserContextStore((state) => state.setError);
export const useResetUserContext = () => useUserContextStore((state) => state.reset);

// Auth state derived selectors
// Anonymous = no user data (regardless of loading state — avoids auth-header flash during fetch)
export const useIsAnonymous = () =>
  useUserContextStore((state) => state.data === null);

// LogoutOnly = authenticated on developer portal network (networkId 50) with no active subscription.
// Network 50 is the ECGrid developer/subscriber portal — only that network requires billing gating.
// Treats null, undefined, and "" subscriptionId as "no subscription" (backend may omit the field).
export const useIsLogoutOnly = () =>
  useUserContextStore((state) => {
    const data = state.data;
    if (!data) return false;
    return (
      data.tenant?.networkId === 50 &&
      (data.billing == null || !data.billing.subscriptionId)
    );
  });

// Display name derived from firstName + lastName, fallback to userName, then empty string.
// Callers should supply a translated 'User' fallback for the empty-string case.
export const useDisplayName = () =>
  useUserContextStore((state) => {
    const d = state.data;
    if (!d) return '';
    return d.firstName && d.lastName ? `${d.firstName} ${d.lastName}` : d.userName || '';
  });

// For testing only
export const _resetUserContextStore = () => useUserContextStore.getState()._reset();
